// Admin Trash bin — browse soft-deleted job attachments, purge
// individual entries, or run "Empty Trash (>30 days)" on demand.
//
// Files land here when the office team picks "Move to Trash" while
// deleting a job day-doc. A nightly cron at 03:15 UTC also auto-purges
// anything older than 30 days; this page lets the admin trigger the
// same cleanup manually if needed.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Trash2,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Folder,
  Undo2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminTrash() {
  const [loading, setLoading] = useState(false);
  const [emptying, setEmptying] = useState(false);
  const [purgingId, setPurgingId] = useState(null);
  const [restoringId, setRestoringId] = useState(null);
  const [data, setData] = useState(null);
  const [confirmEmpty, setConfirmEmpty] = useState(false);

  const fetchTrash = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('fieldflow_token');
      const r = await axios.get(`${API}/admin/trash`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
    } catch (e) {
      toast.error(`Failed to load trash: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Match the JobAudit pattern: don't auto-fetch on mount. Admin clicks
    // Refresh — keeps the page predictable and avoids a useEffect+setState
    // pattern that lint flags as risky.
  }, []);

  const restoreOne = async (trashFolder) => {
    setRestoringId(trashFolder);
    try {
      const token = localStorage.getItem('fieldflow_token');
      const enc = encodeURIComponent(trashFolder);
      const r = await axios.post(`${API}/admin/trash/${enc}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Job ${r.data.job_number || 'restored'} restored.`);
      await fetchTrash();
    } catch (e) {
      toast.error(`Restore failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setRestoringId(null);
    }
  };

  const purgeOne = async (trashFolder) => {
    setPurgingId(trashFolder);
    try {
      const token = localStorage.getItem('fieldflow_token');
      // The folder path contains slashes; encode it so the route param is intact
      const enc = encodeURIComponent(trashFolder);
      await axios.delete(`${API}/admin/trash/${enc}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Entry purged.');
      await fetchTrash();
    } catch (e) {
      toast.error(`Purge failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setPurgingId(null);
    }
  };

  const emptyTrash = async () => {
    setEmptying(true);
    setConfirmEmpty(false);
    try {
      const token = localStorage.getItem('fieldflow_token');
      const r = await axios.post(
        `${API}/admin/trash/empty?older_than_days=30`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(r.data.message);
      await fetchTrash();
    } catch (e) {
      toast.error(`Empty trash failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setEmptying(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-3">
            <Trash2 className="w-6 h-6 text-amber-600" />
            Deleted Jobs &amp; Files
          </CardTitle>
          <p className="text-sm text-slate-600 mt-2">
            Jobs soft-deleted via the &quot;Move to Trash&quot; flow live here for 30 days
            with a full snapshot of the job document. Click <strong>Restore Job</strong> to
            put one back into your active Jobs list. Attached files also move to{' '}
            <code className="bg-slate-100 px-1 rounded">/FieldReady/Jobs/_trash/</code>{' '}
            in OneDrive. Auto-purge runs nightly at 3:15 AM.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 mb-4">
            <Button
              onClick={fetchTrash}
              disabled={loading}
              variant="outline"
              data-testid="refresh-trash-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={() => setConfirmEmpty(true)}
              disabled={emptying || !data || data.expirable_count === 0}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="empty-trash-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {emptying
                ? 'Emptying…'
                : `Empty Trash (>30 days)${data && data.expirable_count > 0 ? ` · ${data.expirable_count}` : ''}`}
            </Button>
          </div>

          {data && (
            <div
              className={`p-4 rounded-lg border-2 mb-4 ${
                data.total === 0
                  ? 'bg-green-50 border-green-300'
                  : 'bg-amber-50 border-amber-300'
              }`}
            >
              <div className="flex items-center gap-3">
                {data.total === 0 ? (
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                )}
                <div className="font-semibold">
                  {data.total === 0
                    ? 'Trash is empty.'
                    : `${data.total} entr${data.total === 1 ? 'y' : 'ies'} in trash · ${data.expirable_count} ready to auto-purge (>30 days)`}
                </div>
              </div>
            </div>
          )}

          {data && data.entries.length > 0 && (
            <div className="space-y-2" data-testid="trash-list">
              {data.entries.map((e) => {
                const isExpired = e.age_days >= 30;
                return (
                  <div
                    key={e.trash_folder}
                    className={`p-4 rounded-lg border ${
                      isExpired ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'
                    }`}
                    data-testid={`trash-entry-${e.job_number || 'unknown'}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="font-mono font-semibold text-amber-700">
                            {e.job_number || '(no number)'}
                          </span>
                          {e.job_title && (
                            <span className="text-slate-700">— {e.job_title}</span>
                          )}
                          <Badge
                            variant="outline"
                            className={
                              isExpired
                                ? 'border-red-400 text-red-700'
                                : 'border-slate-300 text-slate-600'
                            }
                          >
                            <Clock className="w-3 h-3 mr-1" />
                            {e.age_days < 0 ? 'unknown age' : `${e.age_days}d old`}
                          </Badge>
                          <Badge variant="outline" className="border-slate-300">
                            {e.files_count} file{e.files_count === 1 ? '' : 's'}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-600 mt-1">
                          Deleted {new Date(e.deleted_at).toLocaleString()} by{' '}
                          <span className="font-medium">{e.deleted_by || 'unknown'}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1 font-mono flex items-center gap-1.5">
                          <Folder className="w-3 h-3" />
                          {e.onedrive_folder}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1.5 flex-shrink-0">
                        {e.has_snapshot && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-400 text-green-700 hover:bg-green-50"
                            onClick={() => restoreOne(e.trash_folder)}
                            disabled={restoringId === e.trash_folder || purgingId === e.trash_folder}
                            data-testid={`restore-entry-${e.job_number || 'unknown'}`}
                            title="Restore this job back to the active Jobs list"
                          >
                            <Undo2 className="w-3.5 h-3.5 mr-1.5" />
                            {restoringId === e.trash_folder ? 'Restoring…' : 'Restore Job'}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-700 hover:bg-red-50"
                          onClick={() => purgeOne(e.trash_folder)}
                          disabled={purgingId === e.trash_folder || restoringId === e.trash_folder}
                          data-testid={`purge-entry-${e.job_number || 'unknown'}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                          {purgingId === e.trash_folder ? 'Purging…' : 'Purge Now'}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={confirmEmpty} onOpenChange={setConfirmEmpty}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Empty Trash (older than 30 days)?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete OneDrive folders for {data?.expirable_count || 0} entries
              older than 30 days. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={emptyTrash}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-empty-trash-btn"
            >
              Empty Trash
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
